import { useState, useEffect } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { db } from "../../lib/firebase";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import {
  CheckCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  ArrowLeftIcon
} from "../../components/icons/OutlineIcons";
import { Link } from "react-router-dom";
import { useMediaUpload } from "../../hooks/useMediaUpload";

export default function CertificateSettings() {
  const { user } = useAuthContext();
  const { tier, plan } = useSubscription();
  const { uploadMedia } = useMediaUpload();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [certificateType, setCertificateType] = useState<"edumi" | "custom">("edumi");
  const [customCertificateUrl, setCustomCertificateUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;

      try {
        const settingsRef = doc(db, "certificateSettings", user.uid);
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setCertificateType(data.certificateType || "edumi");
          setCustomCertificateUrl(data.customCertificateUrl || "");
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Check file type
    if (!file.type.includes("image") && file.type !== "application/pdf") {
      alert("กรุณาอัปโหลดไฟล์รูปภาพหรือ PDF เท่านั้น");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("ไฟล์มีขนาดใหญ่เกิน 5MB");
      return;
    }

    setUploading(true);

    try {
      // Use a temporary course ID for certificate uploads (user's UID)
      const tempCourseId = user.uid;
      
      // Upload to R2 using the media upload hook
      const result = await uploadMedia(tempCourseId, file, "image");
      
      // Set the uploaded file URL directly from the result
      setCustomCertificateUrl(result.url);
      alert("อัปโหลดสำเร็จ!");
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("เกิดข้อผิดพลาดในการอัปโหลด");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    if (certificateType === "custom" && !customCertificateUrl) {
      alert("กรุณาอัปโหลดใบเซอร์ติฟิเคตของคุณก่อน");
      return;
    }

    setSaving(true);

    try {
      const settingsRef = doc(db, "certificateSettings", user.uid);
      await setDoc(settingsRef, {
        certificateType,
        customCertificateUrl: certificateType === "custom" ? customCertificateUrl : "",
        updatedAt: new Date(),
      });

      alert("บันทึกการตั้งค่าสำเร็จ!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  // Check if user has access to certificates
  const hasCertificateAccess = tier === "yearly";

  if (!hasCertificateAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/instructor/dashboard"
            className="flex items-center gap-2 text-textLight hover:text-primary transition-colors mb-8"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium">กลับไปแดชบอร์ด</span>
          </Link>

          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🔒</span>
            </div>
            <h1 className="text-3xl font-bold text-textDark mb-4">
              ฟีเจอร์นี้ใช้ได้เฉพาะแพ็กเกจ Pro+ ขึ้นไป
            </h1>
            <p className="text-textLight mb-8">
              อัปเกรดแพ็กเกจของคุณเพื่อใช้งานระบบใบเซอร์ติฟิเคตและฟีเจอร์อื่นๆ อีกมากมาย
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all"
              >
                ดูแพ็กเกจ
              </Link>
              <Link
                to="/instructor/dashboard"
                className="bg-gray-200 text-textDark px-8 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
              >
                กลับไปแดชบอร์ด
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Link
          to="/instructor/dashboard"
          className="flex items-center gap-2 text-textLight hover:text-primary transition-colors mb-8"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="font-medium">กลับไปแดชบอร์ด</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-2">
            ตั้งค่าใบเซอร์ติฟิเคต
          </h1>
          <p className="text-textLight text-lg">
            เลือกรูปแบบใบเซอร์ติฟิเคตที่จะออกให้นักเรียน
          </p>
        </div>

        {/* Current Plan Badge */}
        <div className="bg-gradient-primary text-white rounded-2xl p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-6 h-6" />
            <div>
              <p className="font-semibold">แพ็กเกจปัจจุบัน: {plan?.name}</p>
              <p className="text-sm opacity-90">คุณสามารถใช้งานระบบใบเซอร์ติฟิเคตได้</p>
            </div>
          </div>
        </div>

        {/* Certificate Type Selection */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-textDark mb-6">เลือกรูปแบบใบเซอร์ติฟิเคต</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Edumi Certificate */}
            <button
              onClick={() => setCertificateType("edumi")}
              className={`p-6 rounded-2xl border-2 transition-all ${
                certificateType === "edumi"
                  ? "border-primary bg-blue-50 shadow-lg"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  certificateType === "edumi" ? "bg-gradient-primary" : "bg-gray-100"
                }`}>
                  <DocumentTextIcon className={`w-6 h-6 ${
                    certificateType === "edumi" ? "text-white" : "text-gray-400"
                  }`} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-bold text-textDark mb-2">ใบเซอร์ Edumi</h3>
                  <p className="text-sm text-textLight">
                    ใช้แบบฟอร์มใบเซอร์ติฟิเคตมาตรฐานของ Edumi ที่ออกแบบมาอย่างทางการและสวยงาม
                  </p>
                  {certificateType === "edumi" && (
                    <div className="mt-3 flex items-center gap-2 text-primary text-sm font-semibold">
                      <CheckCircleIcon className="w-5 h-5" />
                      กำลังใช้งาน
                    </div>
                  )}
                </div>
              </div>
            </button>

            {/* Custom Certificate */}
            <button
              onClick={() => setCertificateType("custom")}
              className={`p-6 rounded-2xl border-2 transition-all ${
                certificateType === "custom"
                  ? "border-primary bg-blue-50 shadow-lg"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  certificateType === "custom" ? "bg-gradient-primary" : "bg-gray-100"
                }`}>
                  <PhotoIcon className={`w-6 h-6 ${
                    certificateType === "custom" ? "text-white" : "text-gray-400"
                  }`} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-bold text-textDark mb-2">ใบเซอร์ของคุณ</h3>
                  <p className="text-sm text-textLight">
                    อัปโหลดแบบฟอร์มใบเซอร์ติฟิเคตของคุณเอง (รองรับ PDF และรูปภาพ)
                  </p>
                  {certificateType === "custom" && (
                    <div className="mt-3 flex items-center gap-2 text-primary text-sm font-semibold">
                      <CheckCircleIcon className="w-5 h-5" />
                      กำลังใช้งาน
                    </div>
                  )}
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Custom Certificate Upload */}
        {certificateType === "custom" && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-textDark mb-4">อัปโหลดใบเซอร์ติฟิเคต</h2>
            <p className="text-textLight mb-6">
              อัปโหลดแบบฟอร์มใบเซอร์ติฟิเคตของคุณ (รองรับไฟล์ JPG, PNG, PDF ขนาดไม่เกิน 5MB)
            </p>

            <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
              {customCertificateUrl ? (
                <div>
                  <div className="mb-4">
                    {customCertificateUrl.endsWith(".pdf") ? (
                      <div className="w-20 h-20 bg-red-100 rounded-xl flex items-center justify-center mx-auto">
                        <DocumentTextIcon className="w-10 h-10 text-red-600" />
                      </div>
                    ) : (
                      <img
                        src={customCertificateUrl}
                        alt="Certificate Preview"
                        className="max-w-full max-h-64 mx-auto rounded-xl shadow-lg"
                      />
                    )}
                  </div>
                  <p className="text-green-600 font-semibold mb-4">✓ อัปโหลดสำเร็จ</p>
                  <label className="inline-block bg-gray-200 text-textDark px-6 py-2 rounded-xl font-semibold hover:bg-gray-300 transition-all cursor-pointer">
                    เปลี่ยนไฟล์
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              ) : (
                <div>
                  <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <PhotoIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-textLight mb-4">
                    {uploading ? "กำลังอัปโหลด..." : "คลิกเพื่ออัปโหลดไฟล์"}
                  </p>
                  <label className="inline-block bg-gradient-primary text-white px-6 py-2 rounded-xl font-semibold hover:shadow-xl transition-all cursor-pointer">
                    {uploading ? "กำลังอัปโหลด..." : "เลือกไฟล์"}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Link
            to="/instructor/dashboard"
            className="bg-gray-200 text-textDark px-8 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
          >
            ยกเลิก
          </Link>
          <button
            onClick={handleSave}
            disabled={saving || (certificateType === "custom" && !customCertificateUrl)}
            className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "กำลังบันทึก..." : "บันทึกการตั้งค่า"}
          </button>
        </div>
      </div>
    </div>
  );
}
